import { nanoid } from 'nanoid';

export type Callback<T> = (value: T) => void;

export type Unsubscribe = () => void;

export type Subscribe<T> = (callback: Callback<T>) => Unsubscribe;

/**
 * A channel is a stream of values that can be subscribed to.
 */
export class Channel<T = unknown> {
    /** A unique identifier for this channel. */
    readonly id: string = nanoid();

    /** The list of subscribers. */
    private subscribers: readonly Callback<T>[] = [];

    /** Subscribes a callback to future values. Returns an unsubscribe function. */
    subscribe(callback: Callback<T>): Unsubscribe {
        // Guard the callback to prevent it from being called after unsubscribing.
        let isSubscribed = true;
        const guardedSubscriber = (value: T) => {
            if (isSubscribed) {
                callback(value);
            }
        };

        // Add the guarded subscriber to the list of subscribers, without mutating the array.
        this.subscribers = this.subscribers.concat([guardedSubscriber]);

        // Create an unsubscribe function that first triggers the guard, then removes the subscriber.
        const unsubscribe: Unsubscribe = () => {
            isSubscribed = false;
            this.subscribers = this.subscribers.filter((sub) => sub !== guardedSubscriber);
        };

        return unsubscribe;
    }

    /** Pushes a value to all subscribers. */
    push(value: T) {
        for (const subscriber of this.subscribers) {
            subscriber(value);
        }
    }

    /** Removes all subscribers. */
    unsubscribeAll() {
        this.subscribers = [];
    }
}
